import {createApp, h} from 'vue';
import {getActivePinia} from 'pinia';
import ConfirmationBox, {type ConfirmationBoxOptions} from "~/components/global/ConfirmationBox.vue";

// Base function for showing confirmation dialog
const baseConfirm = (options: Partial<ConfirmationBoxOptions>): Promise<void> => {
  return new Promise((resolve, reject) => {
    const confirmEl = document.createElement('div');
    document.body.appendChild(confirmEl);
    document.body.style.overflow = 'hidden';

    const app = createApp({
      render() {
        // Pass the options directly, letting the component handle merging with defaults
        return h(ConfirmationBox, {
          ...options as ConfirmationBoxOptions,
          onConfirm: () => {
            resolve();
            cleanUp();
          },
          onCancel: () => {
            reject();
            cleanUp();
          },
        });
      },
    });

    const pinia = getActivePinia();
    if (pinia) {
      app.use(pinia);
    }

    const cleanUp = () => {
      // Give time for potential animations/transitions to finish
      document.body.style.overflow = '';
      setTimeout(() => {
        app.unmount();
        document.body.removeChild(confirmEl);
      }, 350);
    };

    app.mount(confirmEl);
  });
};

// Wrapper function to include default options and reusable behavior
const $confirm = (title: string, text: string, options: Partial<ConfirmationBoxOptions> = {}): Promise<void> => {
  return baseConfirm({title, text, ...options});
};

// Extending $confirm with specific methods for different types
$confirm.danger = (title: string, text: string, options: Partial<ConfirmationBoxOptions> = {}): Promise<void> => {
  return $confirm(title, text, {
    type: 'danger',
    icon: 'triangle-exclamation',
    size: 'lg',
    avatar: null,
    badges: null,
    persistent: false,
    escapable: true,
    confirm: {
      text: 'Proceed',
      color: 'error',
      visible: true,
    },
    cancel: {
      text: 'Nevermind',
      color: 'outline',
      visible: true,
    },
    ...options,
  });
};

$confirm.success = (title: string, text: string, options: Partial<ConfirmationBoxOptions> = {}, icon:string = 'check-circle'): Promise<void> => {
  return $confirm(title, text, {
    type: 'success',
    icon: icon,
    size: 'lg',
    avatar: null,
    badges: null,
    persistent: false,
    escapable: true,
    confirm: {
      text: 'Proceed',
      color: 'primary',
      visible: true,
    },
    cancel: {
      text: 'Nevermind',
      color: 'outline',
      visible: true,
    },
    ...options,
  });
};

$confirm.warning = (title: string, text: string, options: Partial<ConfirmationBoxOptions> = {}): Promise<void> => {
  return $confirm(title, text, {
    type: 'warning',
    icon: 'exclamation-triangle',
    size: 'lg',
    avatar: null,
    badges: null,
    persistent: false,
    escapable: true,
    confirm: {
      text: 'Proceed',
      color: 'primary',
      visible: true,
    },
    cancel: {
      text: 'Nevermind',
      color: 'outline',
      visible: true,
    },
    ...options,
  });
};

$confirm.error = (title: string, text: string, options: Partial<ConfirmationBoxOptions> = {}, icon:string = 'triangle-exclamation' ): Promise<void> => {
  return $confirm(title, text, {
    type: 'error',
    icon: icon,
    size: 'lg',
    avatar: null,
    badges: null,
    persistent: false,
    escapable: true,
    confirm: {
      text: 'Proceed',
      color: 'primary',
      visible: true,
    },
    cancel: {
      text: 'Nevermind',
      color: 'outline',
      visible: true,
    },
    ...options,
  });
};

export {$confirm};